import { CaretDown } from '@phosphor-icons/react';
import Richtext from '@tmh-website/components/shared/Richtext';
import { Color } from '@tmh-website/shared/colors';
import { SectionBase, resetButtonStylingCSS } from '@tmh-website/styles/global';
import StyledTypo from '@tmh-website/styles/typo';
import { ButtonThemeColor, buttonThemes } from '@tmh-website/ui/Button';
import transparentize from 'polished/lib/color/transparentize';
import styled, { css } from 'styled-components';
import { ContactSupportThemeColor, contactSupportThemes } from './ContactSupport.theme';

export const Section = styled(SectionBase)<{ $themeColor: ContactSupportThemeColor }>`
  color: ${props => props.theme.color[contactSupportThemes[props.$themeColor].text]};
  background-color: ${props => props.theme.color[contactSupportThemes[props.$themeColor].background]};
`;

export const FromContainer = styled.div`
  margin-bottom: ${props => props.theme.gap.medium};
`;

export const AudienceSelectContainer = styled.div`
  max-width: 220px;
  margin: 0 auto;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: ${props => props.theme.gap.medium};
  @media (${props => props.theme.breakpoint.lg.up}) {
    flex-direction: row;
  }

  &:first-child {
    margin-top: 0;
  }
`;

export const Sidebar = styled.div`
  display: flex;
  flex: 0;
  flex-direction: row;
  gap: 6px;
  @media (${props => props.theme.breakpoint.lg.up}) {
    flex: 0 0 200px;
    flex-direction: column;
    align-items: flex-end;
  }
`;

export const SidebarButton = styled.button<{ $themeColor: ButtonThemeColor; $isActive: boolean }>`
  ${resetButtonStylingCSS}
  ${StyledTypo.h6Css};
  display: flex;
  padding: 10px 20px;
  margin: 0;
  color: ${props => props.theme.color[buttonThemes[props.$themeColor].text]};
  text-align: right;
  background-color: ${props =>
    props.$isActive
      ? props.theme.color[buttonThemes[props.$themeColor].background.active]
      : props.theme.color[buttonThemes[props.$themeColor].background.normal]};
  border-radius: ${props => props.theme.borderRadius.large};
  transition: background ${props => props.theme.transitionTiming};

  &:hover {
    background-color: ${props => props.theme.color[buttonThemes[props.$themeColor].background.hover]};
  }

  &:active {
    background-color: ${props => props.theme.color[buttonThemes[props.$themeColor].background.active]};
  }
`;

export const Content = styled.div`
  flex-grow: 1;
`;

export const HeadlineContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 30px;

  > svg {
    flex-shrink: 0;
  }
`;

export const Headline = styled(StyledTypo.Typography)`
  margin: 8px 0 4px !important;
`;

export const AdjustedCaretDown = styled(CaretDown)<{ $themeColor: ContactSupportThemeColor }>`
  transition: all ${props => props.theme.transitionTiming};
  transform: rotate(0deg);
`;

export const Entry = styled.button<{ $themeColor: ContactSupportThemeColor; $isOpen: boolean }>`
  ${resetButtonStylingCSS};
  display: block;
  flex-grow: 1;
  width: 100%;
  padding: 20px ${props => props.theme.wrapperPaddingMobile};
  margin: 16px 0;
  color: ${props => props.theme.color[contactSupportThemes[props.$themeColor].text]};
  text-align: left;
  background-color: ${props => props.theme.color[contactSupportThemes[props.$themeColor].surface]};
  border-radius: ${props => props.theme.borderRadius.small};
  transition: color ${props => props.theme.transitionTiming};
  @media (${props => props.theme.breakpoint.xl.up}) {
    padding: 20px;
  }

  &:hover {
    color: ${props => transparentize(0.4, props.theme.color[contactSupportThemes[props.$themeColor].text])};
  }

  ${props =>
    props.$isOpen &&
    css`
      ${AdjustedCaretDown} {
        transform: rotate(180deg);
        color: ${props => props.theme.color[Color.Blue_500_Ultramarine]};
      }
    `}
`;

export const EntryHeadlineContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const EntryText = styled(Richtext)`
  margin-top: 10px;
`;
